import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import Dashboard from "../../pages/dashboard/Dashboard";
import PatientProfile from "../../pages/dashboard/PatientProfile";
import FormsList from "../../pages/dashboard/forms";  

/* --------------------------------
   Main Sidebar Component
---------------------------------- */

const LeftSidebarMenu = ({ toggleActive, active }) => {
  const location = useLocation();

  const [isDark, setIsDark] = useState(false);
  useEffect(() => {
    const checkDarkTheme = () => {
      const htmlHasDark = document.documentElement.classList.contains("dark-theme");
      setIsDark(htmlHasDark);
    };

    checkDarkTheme();

    const observer = new MutationObserver(checkDarkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  /* ✅ Shared style for all module rows (matches previous accordion header style) */
  const moduleStyle = {
    display: "flex",
    alignItems: "center",
    minHeight: "52px",
    padding: "0 16px",
    borderRadius: "10px",
    transition: "all 0.2s ease",
    textDecoration: "none",
    color: isDark ? "#fff" : "#374151",
    fontSize: "0.975rem",
    fontWeight: 600,
    letterSpacing: "0.3px",
    cursor: "pointer",
    marginBottom: "8px",
    "&:hover": {
      backgroundColor: "rgba(86, 81, 244, 0.06)",
      "& .module-icon": {
        color: "#5651F4",
      },
    },
  };

  const moduleActiveStyle = {
    backgroundColor: "rgba(86, 81, 244, 0.1)",
    color: "#5651F4",
    "& .module-icon": {
      color: "#5651F4",
    },
  };

  /* ✅ Reusable Module Item */
  const ModuleItem = ({ to, icon, label }) => {
    const isActive = location.pathname === to;

    return (
      <Box
        component={Link}
        to={to}
        sx={{
          ...moduleStyle,
          ...(isActive && moduleActiveStyle),
        }}
      >
        <i
          className="material-symbols-outlined module-icon"
          style={{
            marginRight: "12px",
            fontSize: "1.4rem",
            display: "flex",
            alignItems: "center",
            color: isActive
              ? "#5651F4"
              : isDark
              ? "rgba(255,255,255,0.7)"
              : "#6B7280",
            transition: "color 0.2s ease",
          }}
        >
          {icon}
        </i>
        <Typography
          component="span"
          sx={{
            flex: 1,
            fontSize: "0.975rem",
            fontWeight: 600,
            letterSpacing: "0.3px",
          }}
        >
          {label}
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      className={`leftSidebarDark hide-for-horizontal-nav ${isDark ? "dark-theme" : ""}`}
      sx={{
        width: "270px",
        minWidth: "270px",
        height: "100vh",
        position: "fixed",

        top: 0,
        left: { xs: active ? 0 : "-270px", lg: active ? "-270px" : 0 },
        zIndex: 1200,
        backgroundColor: isDark ? "#1a1a2e" : "#FFFFFF",
        borderRight: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F3F4F6",
        overflowY: "auto",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",

        /* Scrollbar styling */
        "&::-webkit-scrollbar": {
          width: "4px",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#D1D5DB",
          borderRadius: "4px",
        },

        // ✅ DARK THEME STYLES
        ...(isDark && {
          "& .sidemenu-link": {
            color: "rgba(255,255,255,0.7) !important",
            fontSize: "0.935rem !important",
            padding: "10px 16px !important",
            borderRadius: "8px",
            transition: "all 0.2s ease",
            fontWeight: 400,
          },
          "& .sidemenu-link:hover": {
            color: "#fff !important",
            backgroundColor: "rgba(255, 255, 255, 0.08) !important",
          },
          "& .sidemenu-link.active": {
            color: "#fff !important",
            backgroundColor: "rgba(86, 81, 244, 0.2) !important",
            borderLeft: "3px solid #5651F4 !important",
            fontWeight: 600,
          },
          "& .mat-summary .title": {
            color: "#fff !important",
            fontSize: "0.975rem !important",
            fontWeight: 600,
            letterSpacing: "0.3px",
          },
          "& .mat-summary i": {
            color: "rgba(255,255,255,0.7) !important",
            fontSize: "1.4rem !important",
          },
          "& .mat-summary .trezo-badge": {
            color: "#fff !important",
            backgroundColor: "rgba(86,81,244,0.3) !important",
            fontSize: "0.75rem !important",
            padding: "3px 8px !important",
            borderRadius: "12px",
            fontWeight: 600,
            minWidth: "24px",
            textAlign: "center",
          },
        }),

        // ✅ LIGHT THEME STYLES
        ...(!isDark && {
          "& .sidemenu-link": {
            fontSize: "0.935rem",
            padding: "10px 16px",
            borderRadius: "8px",
            transition: "all 0.2s ease",
            fontWeight: 400,
            color: "#6B7280",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          },
          "& .sidemenu-link:hover": {
            backgroundColor: "rgba(86, 81, 244, 0.06)",
            color: "#5651F4",
          },
          "& .sidemenu-link.active": {
            backgroundColor: "rgba(86, 81, 244, 0.1)",
            borderLeft: "3px solid #5651F4",
            fontWeight: 600,
            color: "#5651F4",
          },
          "& .mat-summary .title": {
            fontSize: "0.975rem",
            fontWeight: 600,
            letterSpacing: "0.3px",
            color: "#374151",
          },
          "& .mat-summary i": {
            fontSize: "1.4rem",
            color: "#6B7280",
          },
          "& .trezo-badge": {
            fontSize: "0.75rem",
            padding: "3px 8px",
            borderRadius: "12px",
            backgroundColor: "#EEF2FF",
            color: "#5651F4",
            fontWeight: 600,
            minWidth: "24px",
            textAlign: "center",
            lineHeight: "1.4",
          },
        }),
      }}
    >
      <Box className="left-sidebar-menu" sx={{
        display: "flex", flexDirection: "column", height: "100%", position: "static !important",
        width: "100% !important",
        top: "auto !important",
        backgroundColor: "transparent !important",
        transition: "none !important",
        zIndex: "auto !important",
      }}>
        {/* ---------- LOGO ---------- */}
        <Box
          className="logo"
          sx={{
            padding: "20px 24px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid #F3F4F6",
            marginBottom: "12px",
            flexShrink: 0,
          }}
        >
          <Link
            to="/dashboard/forms"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textDecoration: "none",
              width: "100%",
            }}
          >

            <div >
              <img
                src="/images/newlogo2.png"
                alt="logo-icon"
                style={{ height: "100%", width: "100%" }}
              />

            </div>

          </Link>


          {/* Burger menu inline with logo */}
          <Box
            className="burger-menu"
            onClick={toggleActive}
            sx={{
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              width: "32px",
              height: "32px",
              borderRadius: "6px",
              flexShrink: 0,
              marginLeft: "12px",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "rgba(86, 81, 244, 0.08)",
              },
            }}
          >
            <Typography
              component="span"
              className="top-bar"
              sx={{
                display: "block",
                height: "2px",
                width: "18px",
                backgroundColor: isDark ? "#fff" : "#6B7280",
                marginBottom: "4px",
                borderRadius: "2px",
                transition: "all 0.2s ease",
              }}
            />
            <Typography
              component="span"
              className="middle-bar"
              sx={{
                display: "block",
                height: "2px",
                width: "18px",
                backgroundColor: isDark ? "#fff" : "#6B7280",
                marginBottom: "4px",
                borderRadius: "2px",
                transition: "all 0.2s ease",
              }}
            />
            <Typography
              component="span"
              className="bottom-bar"
              sx={{
                display: "block",
                height: "2px",
                width: "18px",
                backgroundColor: isDark ? "#fff" : "#6B7280",
                borderRadius: "2px",
                transition: "all 0.2s ease",
              }}
            />
          </Box>
        </Box>

        {/* ---------- SIDEBAR CONTENT ---------- */}
        <Box
          className="sidebar-inner"
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            marginTop: "15px",
            padding: "0 8px",
          }}
        >
          <Box className="sidebar-menu">

            {/* ✅ 1️⃣ DASHBOARD — Separate Module */}
            <ModuleItem
              to="/dashboard/Dashboard"
              icon="dashboard"
              label="Dashboard"
            />
            <ModuleItem
              to="/dashboard/Dashboard"
              icon="dashboard"
              label="Dashboard"
            />
           
            {/* ✅ 2️⃣ PATIENT PROFILE — Separate Module */}
            <ModuleItem
              to="/dashboard/Patient-Profile"
              icon="account_circle"
              label="Patient Profile"
            />

            {/* ✅ 3️⃣ FORMS — Separate Module */}
            <ModuleItem
              to="/dashboard/forms"
              icon="description"
              label="Forms"
            />
          


          </Box>
        </Box>

        {/* ---------- SIDEBAR FOOTER ---------- */}
        <Box
          sx={{
            padding: "16px 20px",
            borderTop: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid #F3F4F6",
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: isDark ? "rgba(255,255,255,0.3)" : "#9CA3AF",
              textAlign: "center",
            }}
          >
            © 2025 NeuroEZ
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LeftSidebarMenu;